import { useState } from "react";
import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SiteFooter from "@/components/SiteFooter";
import { Check, Phone, MapPin, Mail, Clock, Shield, MessageSquare } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const towns = [
  "Mittagong", "Bowral", "Moss Vale", "Berrima", "Burradoo", "Bundanoon",
  "Robertson", "Goulburn", "Picton", "Tahmoor", "Bargo", "Thirlmere",
];

const faqs = [
  {
    q: "How quickly will someone get back to me?",
    a: "We aim to respond to online enquiries and voicemails within one business day, and phone calls during business hours are usually answered directly. If your system has failed during a cold snap, call us rather than using the form so we can prioritise your booking appropriately.",
  },
  {
    q: "Is the quote really free and no-obligation?",
    a: "Yes. We provide a free, no-obligation on-site quotation for new installations, so you can see exactly what's being proposed and why before committing to anything.",
  },
  {
    q: "Do you charge extra for rural or acreage properties?",
    a: "Access and travel across the wider Southern Highlands and Wollondilly area is generally factored into our standard pricing. If your property has particularly long or difficult driveway access, mention it when you enquire so we can plan the visit properly.",
  },
  {
    q: "What's the difference between an emergency and a standard booking?",
    a: "An emergency booking is for a complete heating or cooling failure that leaves a household without safe indoor temperature control, particularly relevant during a hard frost or heatwave. A standard booking covers routine servicing, quoting and non-urgent repairs, and is scheduled in the normal booking queue.",
  },
  {
    q: "Can you service properties outside the towns listed?",
    a: "Very likely — we cover the broader Southern Highlands and Wollondilly region. If you're unsure whether your address is within range, give us a call and we'll confirm straight away.",
  },
  {
    q: "Do I need to be home for the initial quote visit?",
    a: "Yes, we ask that someone be home so we can assess the rooms, existing wiring or ductwork, and outdoor unit placement, and talk through heating priorities for your specific home.",
  },
  {
    q: "What's the difference between a quote, service, repair and emergency booking?",
    a: "A quote is for a new system or upgrade and starts with a free on-site visit. A service is routine maintenance on an existing system. A repair addresses a fault on a system that's still partially working. An emergency is a complete heating or cooling failure during extreme weather — call us directly for these.",
  },
  {
    q: "What information should I have ready when I call?",
    a: "Your suburb, the rooms you want heated or cooled, details of any existing system, and whether your property has any access restrictions or is heritage-listed. This helps us prepare and quote more accurately.",
  },
  {
    q: "Do you charge extra to travel to towns further from Mittagong like Goulburn or Thirlmere?",
    a: "Travel across our service area is generally built into our standard approach rather than itemised separately, though for bookings on the outer edge of our coverage it's worth calling ahead to confirm the best available timeslot.",
  },
];

const ContactUs = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      toast({ title: "Message Sent!", description: "We'll get back to you as soon as possible." });
      setIsSubmitting(false);
      setFormData({ fullName: "", phone: "", email: "", message: "" });
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-background">
      <PageSEO title="Contact Us | ACG Southern Highlands" description="Get in touch with ACG Southern Highlands for a free no-obligation quote, service booking or general enquiry. Call 0480 841 485 or fill in our online form." />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Contact Us" }]} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "ACG Southern Highlands",
            "telephone": "0480 841 485",
            "email": "info@acgsouthernhighlands.com.au",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Mittagong",
              "addressRegion": "NSW",
              "postalCode": "2575",
              "addressCountry": "AU",
            },
            "areaServed": towns.map((t) => ({ "@type": "City", "name": `${t}, NSW` })),
            "openingHoursSpecification": [
              { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "07:00", "closes": "17:00" },
              { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Saturday"], "opens": "08:00", "closes": "14:00" },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
          }),
        }}
      />

      {/* Hero Banner */}
      <section className="relative overflow-hidden">
        <img
          src="https://airconditioningguys.com.au/uploads/contact_us_banner_image/contact_banner.jpg"
          alt="Contact ACG Southern Highlands"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative container mx-auto px-4 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="w-10 h-10 border-l-4 border-t-4 border-acg-blue mb-6" />
            <h1 className="text-4xl md:text-6xl font-black text-white mb-4">
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl font-semibold text-white/90 mb-6">
              Get in Touch with the ACG Team
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-300 flex-shrink-0" />
                <span className="font-medium">Free No-Obligation Onsite Quotation</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-300 flex-shrink-0" />
                <span className="font-medium">Same Day Installations</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-green-300 flex-shrink-0" />
                <span className="font-medium">Servicing the Southern Highlands</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            {/* Call Us Card */}
            <div className="bg-card rounded-2xl shadow-lg border border-border overflow-hidden group hover:shadow-xl transition-shadow">
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-acg-blue/10 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Phone className="w-9 h-9 text-acg-blue" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Give Us a Call</h2>
                <p className="text-3xl font-black text-primary mb-3">0480 841 485</p>
                <p className="text-muted-foreground text-sm mb-5">Feel free to contact us for any enquiries</p>
                <a
                  href="tel:0480841485"
                  className="inline-flex items-center justify-center gap-2 bg-acg-blue hover:bg-acg-blue/90 text-white font-bold py-3 px-8 rounded-lg transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Call Now
                </a>
              </div>
            </div>

            {/* Head Office Card */}
            <div className="bg-card rounded-2xl shadow-lg border border-border overflow-hidden group hover:shadow-xl transition-shadow">
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-5">
                  <MapPin className="w-9 h-9 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Our Head Office</h2>
                <p className="text-lg font-bold text-foreground mb-1">Mittagong Office</p>
                <p className="text-muted-foreground text-sm mb-5">Mittagong, NSW 2575, Australia</p>
                <a
                  href="https://www.google.com/maps/search/ACG+Southern+Highlands+Mittagong+NSW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-3 px-8 rounded-lg transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  Get Directions
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form + Info */}
          <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-2xl shadow-xl p-8 md:p-10 border border-border">
                <h2 className="text-2xl font-bold text-foreground mb-1">Get in Touch</h2>
                <p className="text-muted-foreground text-sm mb-6">
                  Simply fill in our form below and we'll get back to you as soon as possible
                </p>

                <div className="border-t border-border my-6" />

                <h3 className="text-lg font-bold text-foreground mb-6">Your Contact Details</h3>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      placeholder="Please provide your full name"
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Contact Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="Please provide your contact number"
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="What is your email address?"
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Your Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="How can we help you?"
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
                    />
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded-lg px-4 py-3">
                    <Shield className="w-4 h-4 text-secondary flex-shrink-0" />
                    <span><strong>Your privacy is our priority.</strong> All information will be kept 100% safe & secure.</span>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase text-lg py-4 rounded-lg transition-colors disabled:opacity-60"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="bg-card rounded-2xl p-6 border border-border shadow-sm">
                <h3 className="font-bold text-foreground mb-4">Contact Information</h3>
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <div className="w-9 h-9 bg-acg-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-acg-blue" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-foreground">Phone</p>
                      <a href="tel:0480841485" className="text-sm text-muted-foreground hover:text-primary">0480 841 485</a>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-foreground">Email</p>
                      <a href="mailto:info@acgsouthernhighlands.com.au" className="text-sm text-muted-foreground hover:text-primary">info@acgsouthernhighlands.com.au</a>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="w-9 h-9 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-secondary" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-foreground">Business Hours</p>
                      <p className="text-sm text-muted-foreground">Mon–Fri: 7am – 5pm</p>
                      <p className="text-sm text-muted-foreground">Sat: 8am – 2pm</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="w-9 h-9 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-foreground">Emergency</p>
                      <p className="text-sm text-muted-foreground">After-hours service available</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Quick Links */}
              <div className="bg-acg-charcoal rounded-2xl p-6 text-white">
                <h3 className="font-bold text-lg mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <a
                    href="/schedule-service"
                    className="flex items-center justify-center gap-2 bg-acg-blue hover:bg-acg-blue/90 text-white font-bold py-3 px-6 rounded-lg transition-colors w-full"
                  >
                    Schedule Service
                  </a>
                  <a
                    href="/schedule-quote"
                    className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-3 px-6 rounded-lg transition-colors w-full"
                  >
                    Schedule Quote
                  </a>
                </div>
              </div>

              {/* Reviews */}
              <div className="bg-card rounded-2xl p-6 border border-border shadow-sm">
                <h3 className="font-bold text-foreground mb-4">Customer Reviews</h3>
                <div className="space-y-3">
                  {[
                    { platform: "Google", reviews: "490+", rating: "4.9" },
                    { platform: "ProductReview", reviews: "450+", rating: "4.8" },
                    { platform: "Facebook", reviews: "43+", rating: "5.0" },
                  ].map((r) => (
                    <div key={r.platform} className="flex items-center justify-between bg-muted rounded-lg px-4 py-3">
                      <span className="text-sm font-medium text-foreground">{r.platform}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{r.reviews} reviews</span>
                        <span className="bg-secondary/10 text-secondary font-bold text-sm px-2 py-0.5 rounded">
                          ★ {r.rating}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Extended Info */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground mb-6">Areas We Cover Across the Southern Highlands</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            ACG Southern Highlands is based in Mittagong and services homes and businesses right across the region, including {towns.join(", ")} and surrounding Wollondilly towns. Because our team lives and works locally, we understand the difference between servicing an exposed rural property at 700m elevation and a sheltered in-town cottage — and how that affects heating system selection, sizing and installation.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            If you're just outside these towns, don't hesitate to call — our coverage area extends further across the broader Highlands and Wollondilly districts, and we're happy to confirm whether your address is within range before you book.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">What to Have Ready Before You Call</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            A quick call is far more productive if you can have the following on hand:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
            <li>Your suburb or town, so we can confirm coverage and travel time</li>
            <li>The brand, model and rough age of your existing system (if you have one)</li>
            <li>A short description of the fault, or which rooms you're wanting to heat and cool</li>
            <li>Whether the property has ducted, split system, or no existing air conditioning</li>
            <li>Your preferred timeframe — urgent breakdown, or a routine quote</li>
          </ul>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">What Happens After You Enquire</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Once you call or submit the form, one of our team will get back to you to confirm the details of your enquiry and arrange a suitable time. For new installations and larger jobs, this typically means booking a free, no-obligation on-site quote so we can assess your home's layout, insulation, existing ductwork or wiring, and heating priorities before proposing a system. For servicing and repairs, we'll confirm availability and get a technician out as soon as practical.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">Response Times: Emergency vs Standard Bookings</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We treat a complete heating or cooling failure differently to a routine service or quote request. If your system has stopped working entirely during a frost or heatwave, call us directly on 0480 841 485 and let us know it's urgent — after-hours service is available for genuine emergencies. Standard bookings, including annual servicing, general repairs and new installation quotes, are scheduled during our normal business hours in the order enquiries are received, and we'll always give you an honest estimate of when a technician can attend.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">Access & Parking on Country Properties</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Many Southern Highlands properties sit on acreage, have long gravel driveways, or have gated access — all completely normal for our patch, and nothing our team hasn't dealt with before. If your property has a locked gate, a dog on site, or a driveway unsuitable for a larger service vehicle, just let us know when you book so we can plan the visit and arrive on time without delays.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">Our Quoting Process</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            For new systems, we provide a free, no-obligation on-site quote rather than a phone estimate, because getting the sizing and system selection right for a cold-climate home depends on seeing the actual space, insulation and existing infrastructure. You'll receive a clear, itemised quote so you know exactly what's included before deciding whether to proceed — with no pressure and no obligation.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">Booking Types Explained</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We generally handle four types of bookings. A <strong>quote</strong> is for a new installation, upgrade or staged addition, and always starts with a free, no-obligation on-site visit so we can size the system correctly for your home. A <strong>service</strong> booking covers routine annual or bi-annual maintenance to keep an existing system running efficiently through our cold winters. A <strong>repair</strong> booking is for a system that's underperforming or showing a fault but is still partially working, and is scheduled in our normal queue. An <strong>emergency</strong> booking is for a complete heating or cooling failure during extreme weather — call us directly for these rather than using the online form.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">Per-Town Coverage & Travel Notes</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Mittagong and Bowral are closest to our base and are usually the quickest to schedule. Moss Vale, Burradoo and Berrima are also within easy reach for same-week appointments in most cases. Bundanoon and Robertson sit a little further out toward the escarpment, and while travel takes a bit longer, we service these areas regularly and factor the additional distance into our standard scheduling. Goulburn, Picton, Tahmoor, Bargo and Thirlmere are on the outer edge of our coverage — we're happy to attend, but for urgent bookings in these areas it's worth calling ahead so we can confirm the best available time slot.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Wherever you're located, travel time is generally built into our standard approach rather than charged as a separate line item, though very remote or hard-to-reach properties may be discussed with you directly when booking.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">Information That Helps Us Quote Faster</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            The more detail you can share upfront, the faster and more accurate your quote or repair diagnosis will be. Useful information includes: the number and approximate size of rooms you want heated and cooled, whether the property already has ductwork or wall-mounted units, the age and brand of any existing system, photos of your switchboard, roof space or existing outdoor unit if you have them, and whether the property is heritage-listed or has any access restrictions. None of this is compulsory to make an enquiry, but it means our technician arrives better prepared and can often move more quickly through the on-site assessment.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">Access Notes for Rural & Heritage Properties</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Heritage-listed homes in towns like Berrima, Bowral and Mittagong sometimes come with restrictions on where an outdoor unit or visible ductwork can be placed, and older wall or roof construction can affect how a system is best installed. Let us know if your property is heritage-listed or within a conservation area so we can plan an approach — such as a floor console or bulkhead system — that respects the building's character while still delivering effective heating.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            On rural and acreage properties, things like long unsealed driveways, locked gates, livestock, dogs, or limited mobile reception can all affect a visit. Flagging these details when you book means we can plan arrival times, confirm gate codes or contact arrangements in advance, and avoid delays on the day.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">Business Hours Guidance</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Our standard business hours are Monday to Friday, 7am–5pm, and Saturday 8am–2pm. Calls and enquiries made within these hours generally receive the fastest response. Outside these hours, you're welcome to leave a message, submit the online form, or call regardless if it's a genuine emergency — after-hours service is available for households and businesses with a complete heating or cooling failure. For non-urgent matters raised outside business hours, we'll follow up on the next working day.
          </p>

          <h2 className="text-3xl font-bold text-foreground mt-10 mb-6">Contact FAQ</h2>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-bold text-foreground mb-2">{faq.q}</h3>
                <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default ContactUs;
